/**
 * @file CompanySettingsPage.tsx
 * @brief Company Admin → Settings hub (grid catalog).
 *
 * @details
 * Flush GridBlock catalog matching Reports. Related company config pages
 * (profile, features, billing) are live links. "Data & sharing" holds the
 * live company switch for model improvement (ADR-048, 20-10-2026) and the
 * company data controls (ADR-051, 21-10-2026). The
 * "Coming soon" list (language defaults, retention, SSO, security) is hidden
 * unless env.showComingSoon (DES 21-10-2026): only what works is shown.
 * "Security" holds "Require two-step verification for all members" (B31,
 * 25-10-2026), hidden unless env.companyRequire2fa (after P12 ships).
 *
 * @references
 * - docs/product/admin-control-plane.md
 * - features/company-reports/CompanyReportsPage.tsx (catalog layout)
 * - features/analytics/components/GridBlock.tsx
 *
 * @author DES
 * @date 30-07-2026
 */

import { Link } from "react-router-dom";
import {
  Building2,
  Earth,
  HardDrive,
  KeyRound,
  type LucideIcon,
  Shield,
  FileHeart,
  ToggleRight,
  Zap,
} from "lucide-react";
import { SavedCards } from "@/components/icons/SavedCards";
import { Badge } from "@/components/ui/Badge";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { env } from "@/shared/config/env";
import { cn } from "@/shared/theme/cn";
import { CompanyDataSharingSwitch } from "@/features/model-training/DataSharingCard";
import { DataControlsSection } from "@/features/company-data-controls/DataControlsSection";
import { useCompanyFeatures } from "@/features/company-features/api";
import { CompanyTwoFactorSwitch } from "./CompanyTwoFactorSwitch";

// ----------------------------------------------------------------------
// Catalog entries
// Related live pages + planned settings (placeholder description topics).
// ----------------------------------------------------------------------

type SettingsEntry = {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  to?: string;
  /** Shown only while this company feature is on (and not platform-locked). */
  feature?: "medical_assessment";
};

const RELATED: SettingsEntry[] = [
  {
    id: "profile",
    title: "Company profile",
    description: "Billing email and organisation address on invoices.",
    icon: Building2,
    to: "/company/profile",
  },
  {
    id: "features",
    title: "Features",
    description: "Enable or disable products for your team.",
    icon: ToggleRight,
    to: "/company/features",
  },
  {
    id: "billing",
    title: "Billing",
    description: "Your plan and the corporate cards it pays with.",
    icon: SavedCards, // DES 08-10-2026: app icon, was CreditCard
    to: "/company/billing",
  },
  {
    id: "clinic-software",
    title: "Clinic software",
    description:
      "Link your login to your clinic software so exported reports name you as the examining doctor.",
    icon: FileHeart,
    to: "/company/settings/clinic-software",
    // MEDYX doctor link: only for companies using Medical assessment
    // (opt-in, off by default; DES 21-10-2026).
    feature: "medical_assessment",
  },
  {
    // W29 (27-10-2026): Zapier, its company switch and connections.
    // W31 (27-10-2026): the Asana tile is gone; Asana is a card on this page.
    id: "integrations",
    title: "Integrations",
    description: "Zapier, Make, Asana and AI assistants: send minutes, to-dos and notes to the apps you use.",
    icon: Zap,
    to: "/company/settings/integrations",
  },
];

const UPCOMING: SettingsEntry[] = [
  {
    id: "language-pairs",
    title: "Default language pairs",
    description: "Preferred source and target languages for new sessions.",
    icon: Earth,
  },
  {
    id: "retention",
    title: "Data retention",
    description: "How long recordings, transcripts, and notes are kept.",
    icon: HardDrive,
  },
  {
    id: "sso",
    title: "SSO & identity",
    description: "Single sign-on and identity provider wiring.",
    icon: KeyRound,
  },
  {
    id: "security",
    title: "Security",
    description: "Session policies, access controls, and audit preferences.",
    icon: Shield,
  },
];

// ----------------------------------------------------------------------
// Cell renderers
// ----------------------------------------------------------------------

function LiveSettingsCell({ entry }: { entry: SettingsEntry }) {
  const Icon = entry.icon;
  return (
    <GridCell padded={false}>
      <Link
        to={entry.to!}
        className={cn(
          "group flex h-full flex-col p-4 transition-colors md:p-5",
          "hover:bg-muted/30",
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex h-9 items-center text-brand">
            <Icon className="h-5 w-5" />
          </div>
          <Badge variant="outline">Open</Badge>
        </div>
        <h4 className="mt-3 font-medium group-hover:text-brand">
          {entry.title}
        </h4>
        <p className="mt-1 text-sm text-muted-foreground">{entry.description}</p>
      </Link>
    </GridCell>
  );
}

function UpcomingSettingsCell({ entry }: { entry: SettingsEntry }) {
  const Icon = entry.icon;
  return (
    <GridCell className="opacity-70" padded={false}>
      <div className="flex h-full flex-col p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex h-9 items-center text-muted-foreground">
            <Icon className="h-5 w-5" />
          </div>
          <Badge variant="muted">Soon</Badge>
        </div>
        <h4 className="mt-3 text-base font-medium">{entry.title}</h4>
        <p className="mt-1 text-sm text-muted-foreground">{entry.description}</p>
      </div>
    </GridCell>
  );
}

// ----------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------

export function CompanySettingsPage() {
  const features = useCompanyFeatures();
  const related = RELATED.filter(
    (e) =>
      !e.feature ||
      (features.data?.featureAccess?.[e.feature] === true && features.data?.platformLocks?.[e.feature] !== true),
  );
  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand>
          <h2 className="text-xl font-semibold tracking-tight">
            Company settings
          </h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Your company's data sharing, and links to its profile, features,
            billing and connected tools.
          </p>
        </GridBand>

        {/* ADR-048 (20-10-2026): default off; while off, no member's data is
            used to improve models, whatever they chose in their Profile. */}
        <GridBand className="py-2.5">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Data &amp; sharing
          </h3>
        </GridBand>
        {/* ADR-051 (21-10-2026): keep company meetings inside the company. */}
        <DataControlsSection />
        <CompanyDataSharingSwitch />

        {env.companyRequire2fa ? (
          <>
            <GridBand className="py-2.5">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Security
              </h3>
            </GridBand>
            <CompanyTwoFactorSwitch />
          </>
        ) : null}

        <GridBand className="py-2.5">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Related
          </h3>
        </GridBand>

        <GridCells className="sm:grid-cols-2 xl:grid-cols-3">
          {related.map((entry) => (
            <LiveSettingsCell key={entry.id} entry={entry} />
          ))}
        </GridCells>

        {env.showComingSoon ? (
          <>
            <GridBand className="py-2.5">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Coming soon
              </h3>
            </GridBand>

            <GridCells className="sm:grid-cols-2 xl:grid-cols-3">
              {UPCOMING.map((entry) => (
                <UpcomingSettingsCell key={entry.id} entry={entry} />
              ))}
            </GridCells>
          </>
        ) : null}
      </GridBlock>
    </div>
  );
}
